import PropTypes from 'prop-types';
import { useMemo, useEffect, useState } from 'react';
import axios from 'axios';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography, Tooltip } from '@mui/material';
import Button from '@mui/material/Button';

// project imports
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import { renderFilterTypes } from 'utils/react-table';

import { useFilters, useExpanded, useGlobalFilter, useRowSelect, useSortBy, useTable, usePagination } from 'react-table';

import { Eye, Trash, Add } from 'iconsax-react';

import IconButton from 'components/@extended/IconButton';
import SearchBar from '../components/Searchbar';
import PaginationComponent from '../components/Pagination';

import { useNavigate } from 'react-router';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const server = process.env.REACT_APP_API_URL;

function ReactTable({ columns, data, setUserData }) {
  const theme = useTheme();

  const filterTypes = useMemo(() => renderFilterTypes, []);

  const [filterText, setFilterText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);

  const navigate = useNavigate();

  const { getTableProps } = useTable(
    {
      columns,
      data,
      filterTypes,
      initialState: { pageIndex: 0, pageSize: 10 }
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const [deleteConfirmation, setDeleteConfirmation] = useState({ open: false, id: null });

  // Search
  const filteredData = useMemo(() => {
    if (!filterText) return data;
    return data.filter((row) => {
      return Object.values(row).some((value) => {
        if (!value) return false;
        return value.toString().toLowerCase().includes(filterText.toLowerCase());
      });
    });
  }, [data, filterText]);

  // Pagination
  const paginatedData = useMemo(() => {
    return filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const handleSearchChange = (event) => {
    setFilterText(event.target.value);
  };

  const handleDeleteConfirmation = (id) => {
    setDeleteConfirmation({ open: true, id });
  };

  // Delete Record
  const handleConfirmDelete = async () => {
    const { id } = deleteConfirmation;
    try {
      const token = localStorage.getItem('token');

      await axios.delete(`${server}/api/generalsponsorship/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const updatedUsers = data.filter((user) => user._id !== id);
      setUserData(updatedUsers);
      toast.success('Record Deleted Successfully');
    } catch (error) {
      toast.error('Error deleting record');
      console.error(error);
    }
    setDeleteConfirmation({ open: false, id: null });
  };

  return (
    <>
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" spacing={4} justifyContent="space-between" alignItems="center" sx={{ p: 3, pb: 0 }}>
          <SearchBar filterText={filterText} setFilterText={setFilterText} handleSearchChange={handleSearchChange} />

          <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/apps/create-generalsponsorship')}>
            Add
          </Button>
        </Stack>

        {/* Table */}
        <Table {...getTableProps()}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.Header}>{column.Header}</TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedData.map((row) => (
              <TableRow key={row._id}>
                {/* Photo */}
                <TableCell>
                  <img src={row?.passportPhoto} alt="photo" style={{ width: 60, height: 60, borderRadius: 5, objectFit: 'cover' }} />
                </TableCell>

                {/* Name */}
                <TableCell>
                  <Typography>
                    {row?.name} {row?.middleName} {row?.lastName}
                  </Typography>
                </TableCell>

                {/* Email */}
                <TableCell>
                  <Typography>{row?.email}</Typography>
                </TableCell>

                {/* City */}
                <TableCell>
                  <Typography>{row?.city}</Typography>
                </TableCell>

                {/* Course */}
                <TableCell>
                  <Typography>{row?.preSeaCourseName}</Typography>
                </TableCell>

                {/* Passport */}
                <TableCell>
                  <Typography>{row?.passportNo}</Typography>
                </TableCell>

                {/* Date */}
                <TableCell>
                  <Typography>{new Date(row?.createdAt).toLocaleDateString()}</Typography>
                </TableCell>

                {/* Actions */}
                <TableCell>
                  <Tooltip title="View">
                    <IconButton
                      color="primary"
                      onClick={() =>
                        navigate(`/apps/general-sponsorship-details/${row._id}`, {
                          state: row
                        })
                      }
                    >
                      <Eye />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton color="error" onClick={() => handleDeleteConfirmation(row._id)}>
                      <Trash />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}

            {/* Pagination */}
            <PaginationComponent page={page} setPage={setPage} filteredData={filteredData} rowsPerPage={rowsPerPage} />
          </TableBody>
        </Table>
      </Stack>

      {/* Toast */}
      <ToastContainer />

      {/* Delete Popup */}
      {deleteConfirmation.open && (
        <div className="fixed top-0 left-0 w-full h-full bg-gray-800 bg-opacity-50 flex justify-center items-center">
          <div className="bg-white p-6 rounded-md">
            <p className="mb-4">Are you sure you want to delete this record?</p>

            <div className="flex justify-end">
              <button className="bg-red-500 text-white px-4 py-2 rounded mr-4" onClick={handleConfirmDelete}>
                Confirm
              </button>

              <button className="bg-gray-300 px-4 py-2 rounded" onClick={() => setDeleteConfirmation({ open: false, id: null })}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

ReactTable.propTypes = {
  columns: PropTypes.array,
  data: PropTypes.array,
  setUserData: PropTypes.func
};

const GeneralSponsorship = () => {
  const [UserData, setUserData] = useState([]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${server}/api/generalsponsorship`);
        if (response.data) {
          setUserData(response.data);
        }
      } catch (error) {
        console.error('Error fetching data', error);
      }
    };
    fetchUsers();
  }, []);

  const columns = useMemo(
    () => [
      { Header: 'Photo' },
      { Header: 'Name' },
      { Header: 'Email' },
      { Header: 'City' },
      { Header: 'Course' },
      { Header: 'Passport No.' },
      { Header: 'Date' },
      { Header: 'Actions' }
    ],
    []
  );

  return (
    <MainCard content={false}>
      <ScrollX>
        <ReactTable columns={columns} data={UserData} setUserData={setUserData} />
      </ScrollX>
    </MainCard>
  );
};

export default GeneralSponsorship;